<div class="document-viewer-container">
  <!-- Loading state -->
  @if (isLoading) {
    <div class="flex justify-center items-center h-full">
      <div class="flex flex-col items-center">
        <div class="w-10 h-10 border-4 border-stone-200 border-t-primary-600 rounded-full animate-spin"></div>
        <p class="mt-4 text-stone-500">Chargement du document...</p>
      </div>
    </div>
  }

  <!-- Error state -->
  @if (error) {
    <div class="flex flex-col items-center justify-center w-full h-64 text-center p-5">
      <div class="text-primary-600 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 class="text-lg font-medium text-stone-800 mb-2">Erreur de chargement</h3>
      <p class="text-stone-500 mb-4">{{ error }}</p>
      <button class="px-4 py-2 bg-stone-500 text-white rounded hover:bg-stone-600 transition-colors text-sm" (click)="closeDialog()">Fermer</button>
    </div>
  }

  <!-- Document display -->
  @if (!isLoading && !error) {
    <div class="document-content">
      <div class="flex justify-between items-center pb-5 mb-5 border-b border-stone-200">
        <h3 class="text-lg font-medium text-stone-800 truncate max-w-lg">{{ fileName }}</h3>
        <button
          class="p-2 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded transition-colors"
          title="Télécharger"
          [disabled]="isLoadingDownloadDocument()"
          (click)="downloadDocument()">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
      </div>

      <!-- PDF viewer with fallback -->
      @if (isPdf) {
        <div class="h-full w-full relative">
          <!-- PDF Loading Indicator -->
          @if (pdfLoading()) {
            <div class="absolute inset-0 flex items-center justify-center bg-stone-50 bg-opacity-75 z-10">
              <div class="flex flex-col items-center">
                <div class="w-8 h-8 border-3 border-stone-200 border-t-primary-600 rounded-full animate-spin"></div>
                <p class="mt-2 text-stone-500">Chargement du PDF...</p>
              </div>
            </div>
          }

          <!-- PDF iframe -->
          @if (sanitizedFileUrl()) {
            <iframe
              [src]="sanitizedFileUrl()"
              class="w-full h-[calc(100vh-211px)]"
              frameborder="0"
              (load)="onPdfLoad()"
              (error)="onPdfError()"
              title="Visionneuse PDF">
            </iframe>
          }

          <!-- Fallback download option -->
          @if (pdfLoading() === false && !fileUrl) {
            <div class="h-full w-full flex flex-col items-center justify-center p-4">
              <i class="pi pi-file-pdf text-6xl text-red-500 mb-4"></i>
              <h3 class="text-lg font-medium text-stone-800 mb-2">{{ fileName }}</h3>
              <p class="text-stone-500 mb-4">Le PDF ne peut pas être affiché directement.</p>
              <button class="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm" [disabled]="isLoadingDownloadDocument()" (click)="downloadDocument()">Télécharger le PDF</button>
            </div>
          }
        </div>
      }

      <!-- Image viewer -->
      @if (isImage) {
        <div class="h-full w-full flex items-center justify-center overflow-auto">
          @if (fileUrl) {
            <img
              [src]="fileUrl"
              class="max-w-full max-h-full object-contain w-full h-[calc(100vh-211px)]"
              [alt]="fileName"
              (error)="onImageError()" />
          }
        </div>
      }

      <!-- Office documents (Word, Excel, etc.) -->
      @if (isOfficeDocument) {
        <div class="h-full w-full">
          <div class="flex flex-col items-center justify-center h-64 p-5">
            @if (fileExtension === 'doc' || fileExtension === 'docx') {
              <div class="text-primary-600 mb-4">
                <i class="pi pi-file-word text-6xl"></i>
              </div>
            }
            @if (fileExtension === 'xls' || fileExtension === 'xlsx') {
              <div class="text-primary-600 mb-4">
                <i class="pi pi-file-excel text-6xl"></i>
              </div>
            }
            @if (fileExtension === 'ppt' || fileExtension === 'pptx') {
              <div class="text-primary-600 mb-4">
                <i class="pi pi-file text-6xl"></i>
              </div>
            }
            @if (!isKnownOfficeExtension()) {
              <div class="text-primary-600 mb-4">
                <i class="pi pi-file text-6xl"></i>
              </div>
            }

            <h3 class="text-lg font-medium text-stone-800 mb-2">{{ fileName }}</h3>
            <p class="text-stone-500 mb-4">Ce type de document ne peut pas être affiché directement.</p>
            <button class="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm" [disabled]="isLoadingDownloadDocument()" (click)="downloadDocument()">Télécharger</button>
          </div>
        </div>
      }

      <!-- Other file types -->
      @if (!isPdf && !isImage && !isOfficeDocument) {
        <div class="h-full w-full">
          <div class="flex flex-col items-center justify-center h-64 p-5">
            <i class="pi pi-file text-6xl text-stone-400 mb-4"></i>
            <h3 class="text-lg font-medium text-stone-800 mb-2">{{ fileName }}</h3>
            <p class="text-stone-500 mb-4">Ce type de fichier ne peut pas être affiché directement.</p>
            <button class="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm" [disabled]="isLoadingDownloadDocument()" (click)="downloadDocument()">Télécharger</button>
          </div>
        </div>
      }

      <!-- Download error message -->
      @if (downloadError()) {
        <div class="fixed bottom-0 left-0 right-0 bg-primary-50 text-primary-700 p-3 text-center">
          Impossible de télécharger le fichier. Veuillez réessayer.
          <button class="ml-2 px-2 py-1 text-primary-700 hover:bg-primary-100 rounded text-sm" (click)="downloadError.set(false)">Fermer</button>
        </div>
      }
    </div>
  }
</div>
